import { z } from 'zod';
import {
  EquipmentStatusSchema,
  EquipmentTypeSchema,
  type Equipment,
  type EquipmentType,
} from '../../domain/entities/equipment.ts';
import { STARTER_EQUIPMENT_TYPES } from '../../domain/entities/equipment-starter.ts';
import { DateOnlySchema } from '../../domain/entities/common.ts';
import { MoneySchema } from '../../domain/money.ts';
import { newId } from '../../domain/id.ts';
import { createWrite, expectUnchanged, updateWrite } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * The equipment register (SCRUM-209).
 *
 * The same shape as `cost-codes.ts`, and for the same two reasons:
 *
 * 1. **A duplicate is refused by unit name.** Two machines both called
 *    `EX-04` make every booking and every daily log ambiguous, which is the
 *    whole purpose of a unit name. Make and model may repeat freely — a
 *    company with three identical 320s is ordinary.
 * 2. **Nothing is deleted.** A machine a job costed against two years ago
 *    cannot go away without orphaning that cost, so `retireEquipment` sets
 *    the status and there is no delete.
 *
 * Uniqueness is checked here rather than in a rule, because a rule can see
 * the document being written and cannot ask whether another already holds
 * that name. So it is advisory under concurrency — two people adding
 * `EX-04` at the same moment both pass — exactly as recorded for cost codes
 * in P5-01b. The honest fix is a counter in a transaction, which is
 * overkill for a register two people touch a month.
 */
export class EquipmentError extends Error {
  readonly reason: 'invalid' | 'duplicate' | 'unknownType';

  constructor(reason: 'invalid' | 'duplicate' | 'unknownType', message: string) {
    super(message);
    this.name = 'EquipmentError';
    this.reason = reason;
  }
}

export const SaveEquipmentInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the unit a name or number').max(120),
  typeId: z.string().trim().min(1, 'Choose what kind of machine this is'),
  make: z.string().trim().max(120).optional(),
  model: z.string().trim().max(120).optional(),
  year: z.number().int().min(1900).max(2200).optional(),
  serialNumber: z.string().trim().max(120).optional(),
  sizeClass: z.string().trim().max(120).optional(),
  status: EquipmentStatusSchema.optional(),
  acquiredOn: DateOnlySchema.optional(),
  acquisitionCost: MoneySchema.optional(),
  depreciationCostCodeId: z.string().trim().optional(),
  nextMaintenanceOn: DateOnlySchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type SaveEquipmentInput = z.input<typeof SaveEquipmentInputSchema>;

export interface EquipmentContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

const scope = (context: EquipmentContext) => ({ companyId: context.companyId });

const register = (context: EquipmentContext) =>
  context.data.collection<Equipment>(scope(context), 'equipment');

const types = (context: EquipmentContext) =>
  context.data.collection<EquipmentType>(scope(context), 'equipmentTypes');

/** Drop empty strings, so an untouched optional field is absent not blank. */
function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function fieldErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of error.issues) errors[issue.path.join('.')] ??= issue.message;
  return errors;
}

/** The register, by unit name, so the list reads the way a yard is walked. */
export async function listEquipment(context: EquipmentContext): Promise<Equipment[]> {
  const page = await register(context).list();
  return [...page.items].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

export function subscribeToEquipment(
  context: EquipmentContext,
  onChange: (items: readonly Equipment[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return register(context).subscribe(
    undefined,
    (page) =>
      onChange(
        [...page.items].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
      ),
    onError,
  );
}

/**
 * The catalog.
 *
 * Retired types are left out by default because the common caller is the
 * picker on "Add equipment", and a withdrawn type should not be offered
 * for a new machine. **The uncommon caller matters as much**: a machine
 * recorded last year still names its type, so anything resolving a
 * `typeId` to a name has to ask for the retired ones too or the row goes
 * blank — which is a worse outcome than the type still appearing.
 */
export async function listEquipmentTypes(
  context: EquipmentContext,
  options: { readonly includeRetired?: boolean } = {},
): Promise<EquipmentType[]> {
  const page = await types(context).list();
  return [...page.items]
    .filter((type) => options.includeRetired === true || !type.retiredAt)
    .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
}

export const TypeInputSchema = z.object({
  name: z.string().trim().min(1, 'A kind of machine needs a name').max(120),
  category: z.string().trim().min(1, 'Give it a category, so the list groups').max(80),
  typicalSizes: z.string().trim().max(200).default(''),
  certification: z.string().trim().max(400).default(''),
});
export type TypeInput = z.input<typeof TypeInputSchema>;

/**
 * Add a kind of machine of your own (SCRUM-211).
 *
 * The starter catalog is twenty types from a sourced document and it was
 * the **only** way to put anything in this collection, which the
 * maintainer found by looking for the control to add a twenty-first. The
 * rules always allowed the write; nothing offered it.
 *
 * A type added here carries **no maintenance schedule and no citation**,
 * and that is deliberate rather than unfinished. The starter rows cite
 * OSHA because they came from a document that does; writing a maintenance
 * instruction on somebody's behalf and presenting it in the same field
 * would make an invented line look as sourced as a real one. The screen
 * says where a type came from instead.
 */
export async function createEquipmentType(
  context: EquipmentContext,
  input: TypeInput,
): Promise<EquipmentType> {
  const parsed = TypeInputSchema.safeParse(input);
  if (!parsed.success) throw new EquipmentError('invalid', parsed.error.issues[0]!.message);
  await assertTypeNameIsFree(context, parsed.data.name);

  const now = context.clock.now().toISOString();
  const type = EquipmentTypeSchema.parse({
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    category: parsed.data.category,
    ...(parsed.data.typicalSizes ? { typicalSizes: parsed.data.typicalSizes } : {}),
    ...(parsed.data.certification ? { certification: parsed.data.certification } : {}),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  await types(context).create(type);
  return type;
}

export async function updateEquipmentType(
  context: EquipmentContext,
  existing: EquipmentType,
  input: TypeInput,
): Promise<EquipmentType> {
  const parsed = TypeInputSchema.safeParse(input);
  if (!parsed.success) throw new EquipmentError('invalid', parsed.error.issues[0]!.message);
  await assertTypeNameIsFree(context, parsed.data.name, existing.id);

  const patch = {
    name: parsed.data.name,
    category: parsed.data.category,
    typicalSizes: parsed.data.typicalSizes || undefined,
    certification: parsed.data.certification || undefined,
  };
  const updatedAt = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<EquipmentType>(
      scope(context),
      'equipmentTypes',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

/**
 * Withdraw a kind of machine, or put it back.
 *
 * **Retired, never deleted**, like a cost code and like a machine itself.
 * Every unit already recorded against this type points at it by id, and
 * deleting it would leave those rows naming nothing. Retiring takes it off
 * the picker and leaves the record intact.
 */
export async function retireEquipmentType(
  context: EquipmentContext,
  existing: EquipmentType,
  retired: boolean,
): Promise<EquipmentType> {
  const updatedAt = context.clock.now().toISOString();
  const patch = { retiredAt: retired ? updatedAt : undefined };
  await context.data.commit([
    updateWrite<EquipmentType>(
      scope(context),
      'equipmentTypes',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

/** How many machines name this type, so a retire can say what it affects. */
export async function equipmentUsingType(
  context: EquipmentContext,
  typeId: string,
): Promise<number> {
  const all = await listEquipment(context);
  return all.filter((item) => item.typeId === typeId).length;
}

async function assertTypeNameIsFree(
  context: EquipmentContext,
  name: string,
  exceptId?: string,
): Promise<void> {
  const existing = await listEquipmentTypes(context, { includeRetired: true });
  const clash = existing.find(
    (type) => type.id !== exceptId && type.name.toLowerCase() === name.toLowerCase(),
  );
  if (clash) {
    throw new EquipmentError(
      'duplicate',
      clash.retiredAt
        ? `There is a retired kind of machine called ${clash.name}. Reinstate it rather than adding a second.`
        : `There is already a kind of machine called ${clash.name}.`,
    );
  }
}

/**
 * Put the starter types in, for a company that has none.
 *
 * Idempotent by construction: the starter ids are stable, so running it
 * twice writes the same documents rather than a second set. Offered rather
 * than imposed — a company with its own list does not want twenty-eight
 * types to retire one at a time first.
 */
export async function seedEquipmentTypes(context: EquipmentContext): Promise<EquipmentType[]> {
  const now = context.clock.now().toISOString();
  const audit = {
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };
  const already = new Set((await types(context).list()).items.map((type) => type.id));
  const missing = STARTER_EQUIPMENT_TYPES.filter((starter) => !already.has(starter.id)).map(
    (starter): EquipmentType => ({
      id: starter.id,
      companyId: context.companyId,
      name: starter.name,
      category: starter.category,
      // Spread rather than listed, so a field added to the source
      // document reaches the register by editing one file.
      ...(starter.typicalSizes ? { typicalSizes: starter.typicalSizes } : {}),
      ...(starter.iconType ? { iconType: starter.iconType } : {}),
      ...(starter.certification ? { certification: starter.certification } : {}),
      ...(starter.dailyMaintenance ? { dailyMaintenance: starter.dailyMaintenance } : {}),
      ...(starter.beforeUseInspection ? { beforeUseInspection: starter.beforeUseInspection } : {}),
      ...(starter.afterUse ? { afterUse: starter.afterUse } : {}),
      ...(starter.monthlyMaintenance ? { monthlyMaintenance: starter.monthlyMaintenance } : {}),
      ...(starter.yearlyMaintenance ? { yearlyMaintenance: starter.yearlyMaintenance } : {}),
      ...(starter.sourceUrl ? { sourceUrl: starter.sourceUrl } : {}),
      ...audit,
    }),
  );

  if (missing.length > 0) {
    await context.data.commit(
      missing.map((type) => createWrite<EquipmentType>(scope(context), 'equipmentTypes', type)),
    );
  }
  return missing;
}

async function assertNameIsFree(
  context: EquipmentContext,
  name: string,
  exceptId?: string,
): Promise<void> {
  const existing = await listEquipment(context);
  const clash = existing.find(
    (item) => item.id !== exceptId && item.name.toLowerCase() === name.toLowerCase(),
  );
  if (clash) {
    throw new EquipmentError(
      'duplicate',
      `There is already a unit called ${clash.name}. Unit names have to be unique, because they are what a booking and a daily log point at.`,
    );
  }
}

async function assertTypeExists(context: EquipmentContext, typeId: string): Promise<void> {
  const type = await types(context).get(typeId);
  if (!type) {
    throw new EquipmentError('unknownType', 'That kind of machine is not in the catalog.');
  }
}

export async function createEquipment(
  context: EquipmentContext,
  input: SaveEquipmentInput,
  id = newId(),
): Promise<Equipment> {
  const parsed = SaveEquipmentInputSchema.safeParse(input);
  if (!parsed.success)
    throw new EquipmentError('invalid', Object.values(fieldErrors(parsed.error))[0]!);

  await assertTypeExists(context, parsed.data.typeId);
  await assertNameIsFree(context, parsed.data.name);

  const now = context.clock.now().toISOString();
  const item: Equipment = {
    id,
    companyId: context.companyId,
    name: parsed.data.name,
    typeId: parsed.data.typeId,
    make: optional(parsed.data.make),
    model: optional(parsed.data.model),
    ...(parsed.data.year === undefined ? {} : { year: parsed.data.year }),
    serialNumber: optional(parsed.data.serialNumber),
    sizeClass: optional(parsed.data.sizeClass),
    status: parsed.data.status ?? 'active',
    ...(parsed.data.acquiredOn ? { acquiredOn: parsed.data.acquiredOn } : {}),
    ...(parsed.data.acquisitionCost ? { acquisitionCost: parsed.data.acquisitionCost } : {}),
    depreciationCostCodeId: optional(parsed.data.depreciationCostCodeId),
    ...(parsed.data.nextMaintenanceOn ? { nextMaintenanceOn: parsed.data.nextMaintenanceOn } : {}),
    notes: optional(parsed.data.notes),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  await register(context).create(item);
  return item;
}

/**
 * Save changes to a machine.
 *
 * Takes the record the caller loaded, not just its id, for the reason
 * `updateJob` does: a form posts every field, so guarding with a version
 * read inside this function would guard against nothing and a second
 * person's save would quietly put back what the first had changed.
 */
export async function updateEquipment(
  context: EquipmentContext,
  existing: Equipment,
  input: SaveEquipmentInput,
): Promise<Equipment> {
  const parsed = SaveEquipmentInputSchema.safeParse(input);
  if (!parsed.success)
    throw new EquipmentError('invalid', Object.values(fieldErrors(parsed.error))[0]!);

  await assertTypeExists(context, parsed.data.typeId);
  await assertNameIsFree(context, parsed.data.name, existing.id);

  const now = context.clock.now().toISOString();
  const patch = {
    name: parsed.data.name,
    typeId: parsed.data.typeId,
    make: optional(parsed.data.make),
    model: optional(parsed.data.model),
    year: parsed.data.year,
    serialNumber: optional(parsed.data.serialNumber),
    sizeClass: optional(parsed.data.sizeClass),
    status: parsed.data.status ?? existing.status,
    acquiredOn: parsed.data.acquiredOn,
    acquisitionCost: parsed.data.acquisitionCost,
    depreciationCostCodeId: optional(parsed.data.depreciationCostCodeId),
    nextMaintenanceOn: parsed.data.nextMaintenanceOn,
    notes: optional(parsed.data.notes),
    updatedAt: now,
    updatedBy: context.uid,
  };

  await context.data.commit([
    updateWrite<Equipment>(
      scope(context),
      'equipment',
      existing.id,
      patch,
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch };
}

/**
 * Retire a machine. Nothing is deleted.
 *
 * A sold or scrapped machine is still what a job's costs point at, and a
 * register that could lose one would turn those costs into an orphan.
 */
export async function retireEquipment(
  context: EquipmentContext,
  existing: Equipment,
): Promise<Equipment> {
  if (existing.status === 'retired') return existing;

  const now = context.clock.now().toISOString();
  const patch = { status: 'retired' as const, updatedAt: now, updatedBy: context.uid };
  await context.data.commit([
    updateWrite<Equipment>(
      scope(context),
      'equipment',
      existing.id,
      patch,
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch };
}

export async function getEquipment(context: EquipmentContext, id: string): Promise<Equipment> {
  const found = await register(context).get(id);
  if (!found) throw new NotFoundError('equipment', id);
  return found;
}
